import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import DashboardLayout from "../../../components/DashboardLayout";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import Loader from "../../../components/Loader";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import { getMyInfoAPI } from "../../../services/portal/student/studentService";
import StudentDetailsBox from "../../../components/portal/StudentDetailsBox";
import { submitBankSlipAPI } from "../../../services/portal/student/bankTransferService";
import { createCheckoutSessionAPI } from "../../../services/portal/paymentService";
import {
  convertFileToBase64,
  getMonthNameFromDate,
} from "../../../utility/Common";
import { RoutePaths } from "../../../utility/RoutePaths";

const ClassMonthPaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const classId = searchParams.get("classId");
  const date = searchParams.get("date");
  const month = getMonthNameFromDate(date);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [classDetails, setClassDetails] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [paymentType, setPaymentType] = useState(0);
  const [formData, setFormData] = useState({
    paymentMethod: "",
    receiptNumber: "",
    bankSlip: null,
  });

  const loadClassDetails = useCallback(async () => {
    if (!classId) return;

    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        const classItem = res.Data.find((c) => c.ClassId === Number(classId));
        if (classItem) {
          setClassDetails(classItem);
        } else {
          showErrorToastMsg("Class not found");
        }
      }
    } catch (error) {
      console.error("Error loading class details:", error);
      showErrorToastMsg("Failed to load class details");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  const loadStudentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyInfoAPI();
      if (res && res.IsSuccessful && res.Data) {
        setStudentDetails(res.Data);
      }
    } catch (error) {
      console.error("Error loading student details:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (classId) {
      loadClassDetails();
    }
    if (user) {
      loadStudentDetails();
    }
  }, [classId, user, loadClassDetails, loadStudentDetails]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData({
        ...formData,
        [name]: files[0] || null,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handlePaymentMethodChange = (e) => {
    const paymentMethod = e.target.value;

    if (paymentMethod === "Online") {
      setPaymentType(1);
    } else if (paymentMethod === "Bank Transfer") {
      setPaymentType(2);
    } else if (paymentMethod === "Free Card") {
      setPaymentType(3);
    } else {
      setPaymentType(0);
    }

    setFormData({
      ...formData,
      paymentMethod,
      bankSlip: null,
      receiptNumber: "",
    });
  };

  const loadCheckoutScript = (scriptUrl) => {
    return new Promise((resolve, reject) => {
      // Define required global callbacks before loading the script
      window.errorCallback = function (error) {
        console.error("Checkout error (raw):", error);
        console.error("Checkout error (JSON):", JSON.stringify(error, null, 2));
        showErrorToastMsg("Payment error occurred. Please try again.");
        setSubmitting(false);
      };

      window.cancelCallback = function () {
        setSubmitting(false);
      };

      const existing = document.getElementById("mpgs-checkout-script");
      if (existing) {
        existing.remove();
      }

      const script = document.createElement("script");
      script.id = "mpgs-checkout-script";
      script.src = scriptUrl;
      script.setAttribute("data-error", "errorCallback");
      script.setAttribute("data-cancel", "cancelCallback");
      script.onload = resolve;
      script.onerror = () =>
        reject(new Error("Failed to load checkout script"));
      document.body.appendChild(script);
    });
  };

  const handleSubmitPayment = async () => {
    if (!formData.paymentMethod) {
      showErrorToastMsg("Please select a payment method");
      return;
    }

    if (formData.paymentMethod !== "Online" && !formData.receiptNumber) {
      showErrorToastMsg("Please add receipt number");
      return;
    }

    if (formData.paymentMethod === "Bank Transfer") {
      if (!formData.bankSlip) {
        showErrorToastMsg("Please attach bank slip");
        return;
      }
    }

    setSubmitting(true);

    try {
      if (formData.paymentMethod === "Online") {
        var params = {
          classId: Number(classId),
          paymentDate: date,
          amount: classDetails.ClassFee,
          description: `Payment for ${classDetails.Name} - ${month}`,
        };
        const res = await createCheckoutSessionAPI(params);

        if (res && res.result === "SUCCESS" && res.session) {
          console.log("Checkout session response2:", res);
          const successIndicator = res.successIndicator;
          const sessionId = res.session.id;
          sessionStorage.setItem("paymentSuccessIndicator", successIndicator);

          await loadCheckoutScript(process.env.REACT_APP_PAYMENT_CHECKOUT_URL);

          window.Checkout.configure({ session: { id: sessionId } });
          window.Checkout.showPaymentPage();
        } else {
          showErrorToastMsg("Failed to submit payment");
        }
        // const { sessionId, successIndicator, gatewayUrl } = res.Data;
      } else if (formData.paymentMethod === "Bank Transfer") {
        const bankSlipBase64 = await convertFileToBase64(formData.bankSlip);

        const params = {
          classId: classId,
          date: date,
          amount: classDetails.ClassFee,
          number: formData.receiptNumber,
          image: bankSlipBase64,
          isFreePass: false,
        };

        const uploadRes = await submitBankSlipAPI(params);
        if (uploadRes && uploadRes.IsSuccessful) {
          showSuccessToastMsg(
            uploadRes.Message || "Payment submitted successfully",
          );
          navigate(
            `${RoutePaths.STUDENT_CLASS_DETAILS_PAGE}?classId=${classId}`,
          );
        } else {
          showErrorToastMsg(uploadRes?.Message || "Failed to submit payment");
        }
      } else if (formData.paymentMethod === "Free Card") {
        const params = {
          classId: classId,
          date: date,
          amount: 0,
          number: formData.receiptNumber,
          image: "",
          isFreePass: true,
        };

        const uploadRes = await submitBankSlipAPI(params);
        if (uploadRes && uploadRes.IsSuccessful) {
          showSuccessToastMsg(
            uploadRes.Message || "Request submitted successfully",
          );
          navigate(
            `${RoutePaths.STUDENT_CLASS_DETAILS_PAGE}?classId=${classId}`,
          );
        } else {
          showErrorToastMsg(uploadRes?.Message || "Failed to submit payment");
        }
      } else {
        showErrorToastMsg("Unsupported payment method");
      }
    } catch (error) {
      console.error("Error submitting payment:", error);
      showErrorToastMsg("Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  if (!classId || !month) {
    return (
      <DashboardLayout
        title="Payment"
        subtitle="Class ID and Month are required"
      >
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            Class ID and Month are required
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Payment"
      subtitle={`Payment for ${month} - ${classDetails?.Name || ""}`}
    >
      <div>
        <div className="mb-4">
          <button
            onClick={() =>
              navigate(
                `${RoutePaths.STUDENT_CLASS_MONTH_DETAILS_PAGE}?classId=${classId}&month=${month}`,
              )
            }
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
          >
            ← Back to {month} Schedule
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : (
          <>
            <StudentDetailsBox studentDetails={studentDetails} />
            <ClassDetailsBox classDetails={classDetails} />

            {/* Payment Form */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                Payment Information
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmitPayment();
                }}
              >
                <div className="space-y-6">
                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handlePaymentMethodChange}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select Payment Method</option>
                      <option value="Online">Online</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Free Card">Free Card</option>
                    </select>
                  </div>

                  {/* Online Payment Info */}
                  {paymentType === 1 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-2">
                        Online Payment
                      </h4>
                      <p className="text-sm text-gray-600">
                        You will be redirected to the Bank of Ceylon secure
                        payment page to complete your payment of{" "}
                        <span className="font-semibold">
                          LKR {classDetails?.ClassFee?.toFixed(2)}
                        </span>
                        .
                      </p>

                      <div className="d-flex justify-center items-center">
                        <p className="text-md text-gray-500 mt-8 font-semibold">We Accept Card Payments</p>
                        <div className="flex items-center space-x-4 mt-2">
                          <img
                            src="/images/visacard.jpg"
                            alt="Visa"
                            className="h-8 object-contain"
                          />
                          <img
                            src="/images/mastercard.svg"
                            alt="MasterCard"
                            className="h-10 object-contain"
                            style={{marginLeft:"40px"}}
                          />
                          <img
                            src="/images/chinapay.png"
                            alt="American Express"
                            className="h-20 object-contain"
                          />
                          <img
                            src="/images/bocipgblack.jpg"
                            alt="American Express"
                            className="h-10 object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bank Transfer Form (shown after confirm) */}
                  {paymentType === 2 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        Bank Account Details
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Number: 95760014
                        </label>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Royal econ (pvt)
                        </label>

                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bank of Ceylon
                        </label>

                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maharagama
                        </label>

                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kottawa
                        </label>
                      </div>
                    </div>
                  )}

                  {paymentType === 2 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        {paymentType === 2
                          ? "Bank Transfer Details"
                          : "Free Card Details"}
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {paymentType === 2
                            ? "Receipt Number"
                            : "Free Card Code"}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="receiptNumber"
                          onChange={handleChange}
                          className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>

                      {paymentType === 2 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Attach Bank Slip{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="file"
                            name="bankSlip"
                            onChange={handleChange}
                            accept="image/*"
                            className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required={paymentType === 2}
                          />
                          {formData.bankSlip && (
                            <p className="text-xs text-gray-600 mt-1">
                              Selected: {formData.bankSlip.name}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Accepted formats: Image files (JPG, PNG)
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `${RoutePaths.STUDENT_CLASS_MONTH_DETAILS_PAGE}?classId=${classId}&month=${month}`,
                        )
                      }
                      className="text-sm font-medium px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    {(paymentType === 1 ||
                      paymentType === 2 ||
                      paymentType === 3) && (
                      <button
                        type="submit"
                        disabled={submitting}
                        className={`text-sm font-medium px-6 py-2 rounded-md transition-colors ${
                          submitting
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-primary-600 text-white hover:bg-primary-700"
                        }`}
                      >
                        {submitting
                          ? paymentType === 1
                            ? "Redirecting..."
                            : "Submitting..."
                          : paymentType === 1
                            ? "Pay Now"
                            : "Submit"}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClassMonthPaymentPage;
